import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, startOfMonth, endOfMonth, addMonths, subMonths, addDays, subDays, isToday } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { useFamily } from '../../context/FamilyContext';
import { useAuth } from '../../context/AuthContext';
import { subscribeToCustodySchedules, subscribeToCustodyOverrides } from '@coparent/shared/src/services/custodyService';
import { subscribeToActivities, addExcludedDate } from '@coparent/shared/src/services/activityService';
import { expandCustodySchedule, applyOverrides, custodyToCalendarEvents, buildFullCustodyMap } from '@coparent/shared/src/utils/custodyUtils';
import { expandActivityOccurrences } from '@coparent/shared/src/utils/activityUtils';
import { getChildIds } from '@coparent/shared/src/utils/childUtils';
import CustodyDayOverride from '../custody/CustodyDayOverride';
import ActivityInstanceModal from '../activities/ActivityInstanceModal';
import { HiSwitchHorizontal, HiOutlinePaperAirplane } from 'react-icons/hi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faDollarSign } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

// Returns true if a hex color is perceptually light (text on it should be dark)
const isLightColor = (hex) => {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;
  // Relative luminance (WCAG)
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 0.45;
};

const CalendarView = () => {
  const { family, familyId, familyChildren, childrenMap, getAllParticipants } = useFamily();
  const { currentUser } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [schedules, setSchedules] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [activities, setActivities] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    if (!familyId) return;
    const unsub1 = subscribeToCustodySchedules(familyId, setSchedules);
    const unsub2 = subscribeToCustodyOverrides(familyId, setOverrides);
    const unsub3 = subscribeToActivities(familyId, setActivities);
    return () => { unsub1(); unsub2(); unsub3(); };
  }, [familyId]);

  // Helper to get parent label
  const getParentLabel = useCallback((pid) => {
    if (!family) return '';
    const label = family.settings?.parentLabels?.[pid];
    if (label) return label;
    return family.parentProfiles[pid]?.displayName || '';
  }, [family]);

  const events = useMemo(() => {
    if (!family) return { custodyEvents: [], activityEvents: [] };

    const rangeStart = startOfMonth(subMonths(currentDate, 1));
    const rangeEnd = endOfMonth(addMonths(currentDate, 1));

    let custodyEvents = [];

    if (schedules.length) {
      // Merge all schedules — each is scoped to its own date range via startDate/endDate
      let expanded = {};
      schedules.forEach(schedule => {
        const scheduleExpanded = expandCustodySchedule(schedule, rangeStart, rangeEnd);
        expanded = { ...expanded, ...scheduleExpanded };
      });
      const withOverrides = applyOverrides(expanded, overrides, family.parentIds);
      custodyEvents = custodyToCalendarEvents(
        withOverrides,
        family.parentProfiles,
        family.parentIds
      );
    }

    // Add activity events
    const activityEvents = [];
    activities.forEach(activity => {
      const isRecurring = activity.type !== 'event';
      const occurrences = expandActivityOccurrences(activity, rangeStart, rangeEnd);
      occurrences.forEach(({ date: dateStr, instanceOverride }) => {
        // Build title with times; prepend repeat icon for recurring
        let title = activity.name;
        if (activity.startTime) {
          const timeStr = activity.endTime
            ? `${activity.startTime} – ${activity.endTime}`
            : activity.startTime;
          title = `${activity.name}  ·  ${timeStr}`;
        }
        // Icons are rendered via custom EventComponent instead of title prefix

        // Parse start/end times into proper Date objects for week/day views
        const d = new Date(dateStr + 'T00:00:00');
        let eventStart = new Date(d);
        let eventEnd = new Date(d);
        let isAllDay = true;

        if (activity.startTime) {
          const [startH, startM] = activity.startTime.split(':').map(Number);
          eventStart = new Date(d);
          eventStart.setHours(startH, startM, 0, 0);
          isAllDay = false;

          if (activity.endTime) {
            const [endH, endM] = activity.endTime.split(':').map(Number);
            eventEnd = new Date(d);
            eventEnd.setHours(endH, endM, 0, 0);
          } else {
            eventEnd = new Date(eventStart.getTime() + 60 * 60 * 1000);
          }
        }

        activityEvents.push({
          id: `activity-${activity.id}-${dateStr}`,
          title,
          start: eventStart,
          end: eventEnd,
          allDay: isAllDay,
          resource: {
            type: 'activity',
            isRecurring,
            activityId: activity.id,
            activityName: activity.name,
            childIds: getChildIds(activity),
            startTime: activity.startTime,
            endTime: activity.endTime,
            location: activity.location,
            notes: activity.notes || null,
            cost: activity.cost || null,
            date: dateStr,
            dropoffParentId: instanceOverride.dropoffParentId ?? activity.dropoffParentId,
            pickupParentId: instanceOverride.pickupParentId ?? activity.pickupParentId,
            usesCustodyDefault: activity.usesCustodyDefault,
            hasCost: !!activity.cost,
            hasInstanceOverride: !!Object.keys(instanceOverride).length,
            color: activity.color || '#22c55e'
          }
        });
      });
    });

    return { custodyEvents, activityEvents };
  }, [schedules, overrides, activities, family, currentDate]);

  // Custody breakdown stats for 7-day and 30-day windows
  const custodyBreakdown = useMemo(() => {
    if (!family) return null;
    if (!schedules.length) return null;

    const today = new Date();

    const computeBreakdown = (start, end, rangeDays) => {
      const merged = buildFullCustodyMap(schedules, overrides, family, start, end);

      const counts = {};
      family.parentIds.forEach(pid => { counts[pid] = 0; });

      Object.values(merged).forEach(entry => {
        const parentIndex = typeof entry === 'number' ? entry : entry.parentIndex;
        const parentId = family.parentIds[parentIndex - 1];
        if (parentId) counts[parentId]++;
      });

      return { counts, total: rangeDays };
    };

    return {
      past7: computeBreakdown(subDays(today, 7), subDays(today, 1), 7),
      next7: computeBreakdown(today, addDays(today, 6), 7),
      past30: computeBreakdown(subDays(today, 30), subDays(today, 1), 30),
      next30: computeBreakdown(today, addDays(today, 29), 30),
    };
  }, [schedules, overrides, family]);

  // Build a Map for O(1) custody lookups in dayPropGetter and DateHeader
  const custodyByDate = useMemo(() => {
    const map = new Map();
    events.custodyEvents.forEach(e => {
      map.set(format(e.start, 'yyyy-MM-dd'), e.resource);
    });
    return map;
  }, [events.custodyEvents]);

  const handleSelectSlot = useCallback(({ start }) => {
    setSelectedDate(start);
  }, []);

  const handleSelectEvent = useCallback((event) => {
    if (event.resource?.type === 'activity') {
      setSelectedEvent(event);
    }
  }, []);

  const handleSkipInstance = async () => {
    if (!selectedEvent?.resource) return;
    const { activityId, date } = selectedEvent.resource;
    try {
      await addExcludedDate(familyId, activityId, date, currentUser?.uid || null);
      toast.success('Instance removed');
      setSelectedEvent(null);
    } catch (error) {
      toast.error('Failed to remove instance');
    }
  };

  const dayPropGetter = useCallback((date) => {
    const resource = custodyByDate.get(format(date, 'yyyy-MM-dd'));
    if (!resource) return {};

    const style = {
      backgroundColor: resource.color + '12',
      borderLeft: `3px solid ${resource.color}`,
      opacity: resource.isPending ? 0.6 : 1
    };

    if (resource.isTrip) {
      // Trip block: a heavier solid band so a multi-day range reads as continuous
      style.borderLeft = `4px solid ${resource.color}`;
      style.backgroundColor = resource.color + '22';
    } else if (resource.isOverride && !resource.isPending) {
      // Confirmed single-day swap: dashed border
      style.borderLeft = `3px dashed ${resource.color}`;
      style.backgroundColor = resource.color + '18';
    }

    return { style };
  }, [custodyByDate]);

  const eventPropGetter = useCallback((event) => {
    const bg = event.resource?.color || '#22c55e';
    const light = isLightColor(bg);
    return {
      style: {
        backgroundColor: bg,
        borderRadius: '6px',
        color: light ? '#1E2328' : 'white',
        fontSize: '11px',
        fontWeight: '600',
        border: 'none',
        padding: '1px 6px'
      }
    };
  }, []);

  // Custom event renderer to support colored icons with contrast awareness
  const EventComponent = useCallback(({ event }) => {
    const res = event.resource || {};
    const bg = res.color || '#22c55e';
    const light = isLightColor(bg);
    // Green dollar sign that contrasts with the event background
    const dollarColor = light ? '#15803d' : '#86efac';
    // Recurring icon: use a text symbol instead of emoji for color control
    const iconColor = light ? '#1E2328' : 'white';
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: '3px', overflow: 'hidden' }}>
        {res.isRecurring && <FontAwesomeIcon icon={faArrowsRotate} style={{ color: iconColor, fontSize: '10px', flexShrink: 0 }} />}
        {res.hasCost && <FontAwesomeIcon icon={faDollarSign} style={{ color: dollarColor, fontSize: '11px', flexShrink: 0 }} />}
        {res.childIds?.length > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '1px', flexShrink: 0 }}>
            {res.childIds.slice(0, 3).map(cid => {
              const child = childrenMap[cid];
              return child ? (
                <span
                  key={cid}
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: child.color,
                    border: `1px solid ${light ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.4)'}`,
                    display: 'inline-block',
                  }}
                  title={child.firstName}
                />
              ) : null;
            })}
          </span>
        )}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{event.title}</span>
      </span>
    );
  }, [childrenMap]);

  // Custom toolbar with Back, Today, Next order
  const CustomToolbar = useCallback((toolbar) => {
    return (
      <div className="rbc-toolbar">
        <span className="rbc-btn-group">
          <button type="button" onClick={() => toolbar.onNavigate('PREV')}>Back</button>
          <button type="button" onClick={() => toolbar.onNavigate('TODAY')}>Today</button>
          <button type="button" onClick={() => toolbar.onNavigate('NEXT')}>Next</button>
        </span>
        <span className="rbc-toolbar-label">{toolbar.label}</span>
        <span className="rbc-btn-group">
          {toolbar.views.map(view => (
            <button
              key={view}
              type="button"
              className={toolbar.view === view ? 'rbc-active' : ''}
              onClick={() => toolbar.onView(view)}
            >
              {view.charAt(0).toUpperCase() + view.slice(1)}
            </button>
          ))}
        </span>
      </div>
    );
  }, []);

  // Custom DateHeader to show parent name on each day
  const DateHeader = useCallback(({ date, label }) => {
    const resource = custodyByDate.get(format(date, 'yyyy-MM-dd'));
    const parentLabel = resource?.parentId ? getParentLabel(resource.parentId) : null;
    const today = isToday(date);
    const isSwapped = resource?.isOverride;
    const isPending = resource?.isPending;
    const isTrip = resource?.isTrip;

    return (
      <div className="rbc-date-header-custom">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          {parentLabel && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '2px',
              fontSize: '9px',
              fontWeight: 700,
              color: resource?.color || '#78716c',
              textTransform: 'uppercase',
              letterSpacing: '0.03em',
              lineHeight: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '65%',
            }}>
              {isSwapped && (
                isTrip ? (
                  <HiOutlinePaperAirplane style={{
                    fontSize: '10px',
                    flexShrink: 0,
                    transform: 'rotate(45deg)',
                    color: isPending ? '#d97706' : resource?.color || '#78716c',
                  }} />
                ) : (
                  <HiSwitchHorizontal style={{
                    fontSize: '10px',
                    flexShrink: 0,
                    color: isPending ? '#d97706' : resource?.color || '#78716c',
                  }} />
                )
              )}
              {parentLabel}
            </span>
          )}
          {!parentLabel && <span />}
          {today ? (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '1.75rem',
              height: '1.75rem',
              backgroundColor: '#e06449',
              color: '#fff',
              borderRadius: '9999px',
              fontWeight: 800,
              fontSize: '13px',
              lineHeight: 1,
              boxShadow: '0 1px 4px rgba(224, 100, 73, 0.35)',
            }}>
              {label}
            </span>
          ) : (
            <span style={{ fontWeight: 600, fontSize: '13px', color: '#44403c' }}>
              {label}
            </span>
          )}
        </div>
        {isPending && (
          <div style={{
            fontSize: '8px',
            fontWeight: 700,
            color: '#d97706',
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
            textAlign: 'center',
            lineHeight: 1,
            marginTop: '1px',
          }}>
            {isTrip ? 'Trip Pending' : 'Swap Pending'}
          </div>
        )}
      </div>
    );
  }, [custodyByDate, getParentLabel]);

  // Week/Day view header — shows parent name and swap indicator in the column header
  const WeekDayHeader = useCallback(({ date, label }) => {
    const resource = custodyByDate.get(format(date, 'yyyy-MM-dd'));
    const parentLabel = resource?.parentId ? getParentLabel(resource.parentId) : null;
    const isSwapped = resource?.isOverride;
    const isPending = resource?.isPending;
    const isTrip = resource?.isTrip;

    return (
      <div style={{ textAlign: 'center', padding: '2px 0' }}>
        <div style={{ fontWeight: 600, fontSize: '13px', color: '#44403c' }}>{label}</div>
        {parentLabel && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '2px',
            fontSize: '9px',
            fontWeight: 700,
            color: resource?.color || '#78716c',
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
            marginTop: '1px',
          }}>
            {isSwapped && (
              isTrip ? (
                <HiOutlinePaperAirplane style={{
                  fontSize: '10px',
                  transform: 'rotate(45deg)',
                  color: isPending ? '#d97706' : resource?.color || '#78716c',
                }} />
              ) : (
                <HiSwitchHorizontal style={{
                  fontSize: '10px',
                  color: isPending ? '#d97706' : resource?.color || '#78716c',
                }} />
              )
            )}
            {parentLabel}
          </div>
        )}
        {isPending && (
          <div style={{
            fontSize: '8px',
            fontWeight: 700,
            color: '#d97706',
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
            marginTop: '1px',
          }}>
            {isTrip ? 'Trip Pending' : 'Swap Pending'}
          </div>
        )}
      </div>
    );
  }, [custodyByDate, getParentLabel]);

  return (
    <div>
      {/* Legend */}
      {family && (
        <div className="flex items-center gap-5 mb-5">
          {family.parentIds.map(pid => {
            const profile = family.parentProfiles[pid];
            return (
              <div key={pid} className="flex items-center gap-2">
                <div
                  className="w-2.5 h-2.5 rounded-full ring-2 ring-white shadow-warm-sm"
                  style={{ backgroundColor: profile?.color }}
                />
                <span className="text-sm text-ink-500 font-medium">{getParentLabel(pid)}</span>
              </div>
            );
          })}
          <div className="flex items-center gap-1.5 text-ink-400">
            <HiSwitchHorizontal className="text-sm" />
            <span className="text-sm font-medium">Swapped</span>
          </div>
          {familyChildren.length > 0 && (
            <>
              <div className="w-px h-4 bg-surface-200" />
              {familyChildren.map(child => {
                const c = childrenMap[child.id];
                return (
                  <div key={child.id} className="flex items-center gap-1.5">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: c?.color }}
                    />
                    <span className="text-sm text-ink-500 font-medium">{child.firstName}</span>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* Custody Breakdown */}
      {custodyBreakdown && family && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {[
            { label: 'Past 7 days', data: custodyBreakdown.past7 },
            { label: 'Next 7 days', data: custodyBreakdown.next7 },
            { label: 'Past 30 days', data: custodyBreakdown.past30 },
            { label: 'Next 30 days', data: custodyBreakdown.next30 },
          ].map(({ label, data }) => (
            <div key={label} className="card p-4">
              <p className="text-xs font-semibold text-ink-400 uppercase tracking-wide mb-3">{label}</p>
              {family.parentIds.map(pid => {
                const profile = family.parentProfiles[pid];
                const count = data.counts[pid] || 0;
                const pct = data.total > 0 ? Math.round((count / data.total) * 100) : 0;
                return (
                  <div key={pid} className="mb-2 last:mb-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-ink-600">{getParentLabel(pid)}</span>
                      <span className="text-sm font-semibold text-ink-700">{count}d <span className="text-ink-400 font-normal">({pct}%)</span></span>
                    </div>
                    <div className="h-2 rounded-full bg-surface-200 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${pct}%`, backgroundColor: profile?.color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      <div className="card p-5" style={{ height: 650 }}>
        <Calendar
          localizer={localizer}
          events={events.activityEvents}
          date={currentDate}
          onNavigate={setCurrentDate}
          view={view}
          onView={setView}
          views={['month', 'week', 'day']}
          selectable
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectEvent}
          dayPropGetter={dayPropGetter}
          eventPropGetter={eventPropGetter}
          components={{
            toolbar: CustomToolbar,
            event: EventComponent,
            month: {
              dateHeader: DateHeader,
            },
            week: {
              header: WeekDayHeader,
            },
            day: {
              header: WeekDayHeader,
            }
          }}
          scrollToTime={new Date()}
          popup
          style={{ height: '100%' }}
        />
      </div>

      {selectedDate && (
        <CustodyDayOverride
          date={selectedDate}
          custodyParentId={custodyByDate.get(format(selectedDate, 'yyyy-MM-dd'))?.parentId || null}
          onClose={() => setSelectedDate(null)}
        />
      )}

      {/* Activity instance detail modal */}
      {selectedEvent && selectedEvent.resource?.type === 'activity' && (
        <ActivityInstanceModal
          event={selectedEvent}
          family={family}
          familyId={familyId}
          childrenMap={childrenMap}
          allParticipants={getAllParticipants()}
          onClose={() => setSelectedEvent(null)}
          onSkip={handleSkipInstance}
        />
      )}
    </div>
  );
};

export default CalendarView;
